import { SerializedInventoryMeterReadingGroup } from '@enmon/commons/lib/tools';
import { Table, TablePaginationConfig } from 'antd';
import { FilterConfirmProps, FilterValue, SorterResult } from 'antd/lib/table/interface';
import { useDeleteInventoryMeterReadingGroup } from 'hooks/api/reading-groups/useDelete';
import { useInventoryMeterReadingGroups } from 'hooks/api/reading-groups/useFind';
import { useMemo, useState, VFC } from 'react';
import { useIntl } from 'react-intl';
import { ReadingGroupFilter } from 'services/api/inventoryMeterReadingGroups';
import { useStore as useInventorizeStore } from 'routes/inventorize_meter/useStore';
import { DEFAULT_FILTER, DEFAULT_PAGINATION, ReadingGroupListPagination } from '../constants';
import { getColumns } from '../utils/getColumns';

export const ReadingGroupListTable: VFC = () => {
  const intl = useIntl();
  const { location } = useInventorizeStore();
  const [filter, setFilter] = useState<ReadingGroupFilter>({
    ...DEFAULT_FILTER,
    location: location?.id,
  });

  const [pagination, setPagination] = useState<ReadingGroupListPagination>(DEFAULT_PAGINATION);
  const { current, pageSize, total } = pagination;
  // NOTE: memoize valid pagination for request, otherwise refetch would happen after updating total
  const requestPagination = useMemo(() => ({ current, pageSize }), [current, pageSize]);

  const { data: response, isLoading } = useInventoryMeterReadingGroups(
    {
      filter,
      pagination: requestPagination,
      onSuccess: (_, meta) => {
        if (total !== meta.pagination?.total)
          setPagination?.(prev => ({
            ...prev,
            total: meta.pagination?.total ?? DEFAULT_PAGINATION.total,
          }));
      },
    },
    { enabled: !!location?.id },
  );
  const readingGroups = response?.data ?? [];

  const { mutate: deleteGroup, isLoading: isDeletingGroup } = useDeleteInventoryMeterReadingGroup();

  const handleDeleteGroup = (groupId: number) => {
    deleteGroup({ id: groupId });
  };

  const handleSearch = (
    selectedKeys: React.Key[],
    confirm: (param?: FilterConfirmProps) => void,
    item: keyof ReadingGroupFilter,
  ) => {
    confirm();
    setFilter(prev => ({
      ...prev,
      [item]: selectedKeys.length ? selectedKeys[0] : undefined,
    }));
  };

  const handleReset = (clearFilters: () => void, item: keyof ReadingGroupFilter) => {
    clearFilters();
    setFilter(prev => ({
      ...prev,
      [item]: DEFAULT_FILTER[item],
    }));
  };

  const handleTableChange = (
    changedPagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<SerializedInventoryMeterReadingGroup> | SorterResult<SerializedInventoryMeterReadingGroup>[],
  ) => {
    const { field, order } = Array.isArray(sorter) ? sorter[0] ?? {} : sorter;

    setFilter(prev => ({
      ...prev,
      sortOrder: order ?? DEFAULT_FILTER.sortOrder,
      sortField: field ? String(field) : DEFAULT_FILTER.sortField,
    }));

    setPagination(prev => ({
      ...prev,
      current: changedPagination.current ?? prev.current,
      pageSize: changedPagination.pageSize ?? prev.pageSize,
    }));
  };

  return (
    <Table
      columns={getColumns({
        filter,
        onSearch: handleSearch,
        onReset: handleReset,
        intl,
        onDelete: handleDeleteGroup,
        isLoading: isDeletingGroup,
      })}
      dataSource={readingGroups}
      loading={isLoading}
      pagination={pagination}
      rowKey="id"
      scroll={{ x: 'max-content' }}
      onChange={handleTableChange}
    />
  );
};
